import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { tradePackagesAPI, playersAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { ShellRail } from '../../shell/AppShell';
import { SleeperAvatar } from '../../shell/SleeperAvatar';
import UpsellCard from '../../shell/UpsellCard';
import { ReviewIcon } from '../../shell/icons';
import TradeRatingView from './Rating/TradeRatingView';
import TradeThumbnailCard, { getVerdict } from './Rating/TradeThumbnailCard';

/**
 * PackageView — the public trade-package page at /trade-packages/:unique_link
 * (PATH IMMUTABLE — recipients open it from shared links + emails).
 * Shell-native surface; all data flow, role detection, and the rating state
 * machine are unchanged from the legacy page.
 *
 * Render states:
 *  - loading / error
 *  - creator overview (pending → share row; graded → verdicts; counter-proposed → action required + rounds)
 *  - recipient overview (pending → grade CTA + options; graded/countered → verdicts)
 *  - rating flow (?mode=rating) → TradeRatingView per option
 *  - submitted → thank-you
 */

const VIEW_MAX = 880;

const responsiveCss = `
  @media (max-width: 760px) {
    .ftpk-cta { width: 100%; justify-content: center; }
    .ftpk-share { flex-direction: column; align-items: stretch !important; }
    .ftpk-share .ft-btn { justify-content: center; }
  }
`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const ordinalRound = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return `${n}`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

const itemLabel = (item, playerData) => {
  if (item.type === 'player') return playerData[item.playerId]?.name || 'Unknown player';
  if (item.pickData) return `${item.pickData.season} ${ordinalRound(item.pickData.round)}`;
  return 'Draft pick';
};

const PackageView = () => {
  const { unique_link } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useState(new URLSearchParams(window.location.search));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tradePackage, setTradePackage] = useState(null);
  const [playerData, setPlayerData] = useState({});
  const viewMode = searchParams.get('mode') || 'overview'; // 'overview' | 'rating'
  const [currentRatingIndex, setCurrentRatingIndex] = useState(0);
  const [grades, setGrades] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if current user is the creator
  const isCreator = user && tradePackage && user.id === tradePackage.creatorUserId;

  // Prefer sleeperUsername; never expose an email-like fallback in the UI.
  const safeCreatorName =
    tradePackage?.creator?.sleeperUsername ||
    (tradePackage?.creatorDisplayName && !tradePackage.creatorDisplayName.includes('@')
      ? tradePackage.creatorDisplayName
      : 'A manager');

  const setViewMode = (mode) => {
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'overview') {
      newParams.delete('mode');
    } else {
      newParams.set('mode', mode);
    }
    setSearchParams(newParams);
    window.history.pushState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
  };

  useEffect(() => {
    fetchTradePackage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unique_link]);

  const fetchTradePackage = async () => {
    try {
      const response = await tradePackagesAPI.getByLink(unique_link);
      const pkg = response.data;
      setTradePackage(pkg);

      // Initialize grades, notes, and reasons arrays
      setGrades(new Array(pkg.trades.length).fill(null));
      setNotes(new Array(pkg.trades.length).fill(''));
      setReasons(new Array(pkg.trades.length).fill(null).map(() => []));

      // Fetch player data from current trades
      const allPlayerIds = new Set();
      pkg.trades.forEach((trade) => {
        trade.creatorItems.forEach((item) => {
          if (item.playerId) allPlayerIds.add(item.playerId);
        });
        trade.recipientItems.forEach((item) => {
          if (item.playerId) allPlayerIds.add(item.playerId);
        });
      });

      // Also fetch player data from original grades (if counter-proposal)
      if (pkg.originalGrades && pkg.originalGrades.length > 0) {
        pkg.originalGrades.forEach((gradeData) => {
          const trade = gradeData.trade;
          trade.creatorItems?.forEach((item) => {
            if (item.type === 'player' && item.playerId) {
              allPlayerIds.add(item.playerId);
            }
          });
          trade.recipientItems?.forEach((item) => {
            if (item.type === 'player' && item.playerId) {
              allPlayerIds.add(item.playerId);
            }
          });
        });
      }

      if (allPlayerIds.size > 0) {
        const playerResponse = await playersAPI.getBatch(Array.from(allPlayerIds));
        setPlayerData(playerResponse.data.players);
      }
    } catch (err) {
      console.error('Error loading trade package:', err);
      console.error('Error response:', err.response?.data);
      setError('Failed to load trade package');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRating = () => {
    setViewMode('rating');
    setCurrentRatingIndex(0);
  };

  const handleRatingChange = (value) => {
    const newGrades = [...grades];
    newGrades[currentRatingIndex] = value;
    setGrades(newGrades);

    if (value > 5 && (reasons[currentRatingIndex] || []).length > 0) {
      const newReasons = [...reasons];
      newReasons[currentRatingIndex] = [];
      setReasons(newReasons);
    }
  };

  const handleNotesChange = (value) => {
    const newNotes = [...notes];
    newNotes[currentRatingIndex] = value;
    setNotes(newNotes);
  };

  const handleReasonsChange = (next) => {
    const newReasons = [...reasons];
    newReasons[currentRatingIndex] = next;
    setReasons(newReasons);
  };

  const handleNext = async () => {
    if (currentRatingIndex < tradePackage.trades.length - 1) {
      // Add a small delay for smooth transition
      await new Promise(resolve => setTimeout(resolve, 50));
      setCurrentRatingIndex(currentRatingIndex + 1);
    }
  };

  const handlePrevious = async () => {
    if (currentRatingIndex > 0) {
      // Add a small delay for smooth transition
      await new Promise(resolve => setTimeout(resolve, 50));
      setCurrentRatingIndex(currentRatingIndex - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate all trades have grades
    if (grades.some((grade) => grade === null || grade === undefined)) {
      setError('Please rate all trades');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        grades: grades.map((grade, index) => ({
          tradeId: tradePackage.trades[index].id,
          gradeValue: grade,
          notes: notes[index] || null,
          reasons: reasons[index] || [],
        })),
        interests: [],
      };

      await tradePackagesAPI.grade(unique_link, payload);

      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="ft-view" style={{ maxWidth: VIEW_MAX }}>
        <div className="ft-loading">Opening the package…</div>
      </div>
    );
  }

  if (error && !tradePackage) {
    return (
      <div className="ft-view" style={{ maxWidth: VIEW_MAX }}>
        <span className="ft-k brand">Trade Package</span>
        <h1 className="ft-hero" style={{ fontSize: 30 }}>Nothing here.</h1>
        <p className="ft-sub">{error}. Check the link and try again.</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="ft-view" style={{ maxWidth: VIEW_MAX }}>
        <Helmet>
          <title>Trade Package — Fantasy Taco</title>
        </Helmet>
        <div style={{ maxWidth: 560, margin: '40px auto 0', textAlign: 'center' }}>
          <span className="ft-k brand">Trade Package</span>
          <h1 className="ft-hero" style={{ fontSize: 34, marginTop: 8 }}>Thank you.</h1>
          <p className="ft-sub">Your verdict is in. {safeCreatorName} will see every grade and note.</p>
          <button
            className="ft-btn"
            style={{ marginTop: 28 }}
            onClick={() => navigate(user ? '/trade-packages/dashboard' : '/')}
          >
            Done
          </button>
        </div>

        <div className="ft-section" style={{ maxWidth: 560, margin: '64px auto 0' }}>
          <span className="ft-k">While you're here</span>
          <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {[
              {
                kicker: 'Trade Builder',
                title: 'Pitch one of your own.',
                desc: 'Bundle players and picks into a link. Send it back.',
                href: '/trade-packages',
              },
              {
                kicker: 'Manager Intel',
                title: 'Scout a manager.',
                desc: 'Their full dynasty trading history before your next offer.',
                href: '/intel',
              },
            ].map((card) => (
              <button
                key={card.href}
                className="ft-leaguecard"
                onClick={() => navigate(card.href)}
              >
                <span className="ft-k" style={{ display: 'block', marginBottom: 6 }}>{card.kicker}</span>
                <div className="nm">{card.title}</div>
                <div className="tm" style={{ marginBottom: 0 }}>{card.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const currentTrade = tradePackage.trades[currentRatingIndex];
  const status = tradePackage.status;
  const trades = tradePackage.trades || [];
  const leagueName = tradePackage.leagueName || '';
  const recipientName = tradePackage.recipientDisplayName || 'your league mate';
  const viewerRole = isCreator ? 'creator' : 'recipient';

  if (viewMode === 'rating' && currentTrade) {
    return (
      <div className="ft-view" style={{ maxWidth: VIEW_MAX }}>
        <Helmet>
          <title>Trade Package — Fantasy Taco</title>
        </Helmet>
        <ShellRail>
          <div className="ft-railsec">
            <span className="ft-k">Your verdicts</span>
            {trades.map((t, i) => (
              <div className="ft-progressrow" key={t.id}>
                <span style={{ fontWeight: i === currentRatingIndex ? 600 : 400 }}>
                  Option {i + 1}
                </span>
                {grades[i] ? (
                  <span className="ft-chip dark">{grades[i]}/10</span>
                ) : (
                  <span className="ft-chip outline">—</span>
                )}
              </div>
            ))}
          </div>
          <div className="ft-railsec">
            <span className="ft-k">How it works</span>
            <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', lineHeight: 1.5, margin: 0 }}>
              Grade each option 1 to 10. Low grades can carry a reason and a note — {safeCreatorName} sees all of it.
            </p>
          </div>
        </ShellRail>
        <TradeRatingView
          key={currentRatingIndex}
          trade={currentTrade}
          playerData={playerData}
          league={tradePackage.league}
          tweets={tradePackage.tweets || []}
          currentIndex={currentRatingIndex}
          totalTrades={trades.length}
          rating={grades[currentRatingIndex]}
          notes={notes[currentRatingIndex] || ''}
          reasons={reasons[currentRatingIndex] || []}
          onRatingChange={handleRatingChange}
          onNotesChange={handleNotesChange}
          onReasonsChange={handleReasonsChange}
          onBack={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
          isLastTrade={currentRatingIndex === trades.length - 1}
          uniqueLink={unique_link}
          isCreator={isCreator}
          submitError={error}
          submitting={submitting}
        />
      </div>
    );
  }

  // ---------- Overview ----------
  const shouldShowRating =
    (!isCreator && status === 'pending') ||
    (isCreator && status === 'counter-proposed');
  const shouldShowShareLink = isCreator && status === 'pending';
  const showCreatorStatus = isCreator && (status === 'graded' || status === 'counter-proposed');
  const hasHistory = !!(tradePackage.originalGrades && tradePackage.originalGrades.length > 0);

  const gradedValues = trades.filter((t) => t.grade).map((t) => t.grade.value);
  const avgGrade = gradedValues.length
    ? (gradedValues.reduce((s, v) => s + v, 0) / gradedValues.length).toFixed(1).replace(/\.0$/, '')
    : null;

  const statusChip =
    status === 'graded' ? (
      <span className="ft-chip green">Graded{avgGrade ? ` · ${avgGrade} avg` : ''}</span>
    ) : status === 'counter-proposed' ? (
      <span className="ft-chip brand">Counter-proposal</span>
    ) : (
      <span className="ft-chip outline">{isCreator ? 'Awaiting grades' : 'Awaiting your grades'}</span>
    );

  const optionsHeading = (() => {
    if (showCreatorStatus && hasHistory) {
      return status === 'graded'
        ? `Round 2 — ${recipientName}'s counter, graded`
        : `Round 2 — ${recipientName}'s counter`;
    }
    if (status === 'graded') return isCreator ? 'Their verdicts' : 'Your verdicts';
    if (status === 'counter-proposed') return 'The counter-proposal';
    return 'The options';
  })();

  const railSteps = isCreator
    ? ['You send the link', 'They grade each option, 1 to 10', 'You learn what actually moves them']
    : [`${safeCreatorName} sent you ${trades.length === 1 ? 'an offer' : 'options'}`, 'You grade each one, 1 to 10', 'They see your verdicts'];

  return (
    <div className="ft-view" style={{ maxWidth: VIEW_MAX }}>
      <Helmet>
        <title>Trade Package — Fantasy Taco</title>
      </Helmet>
      <style>{responsiveCss}</style>

      <ShellRail>
        {shouldShowShareLink && (
          <div className="ft-railsec">
            <span className="ft-k">Share it</span>
            <button className="ft-railaction" onClick={handleCopyLink}>
              <span>{copied ? 'Copied' : 'Copy the link'}</span>
            </button>
            <p style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', margin: '6px 0 0', lineHeight: 1.5 }}>
              Text it, email it, or drop it in the league chat.
            </p>
          </div>
        )}
        <div className="ft-railsec">
          <span className="ft-k">What happens</span>
          {railSteps.map((step, i) => (
            <div
              key={i}
              style={{
                display: 'flex', gap: 10, padding: '9px 0', fontSize: 13, color: 'var(--ft-ink-2)',
                borderBottom: i < railSteps.length - 1 ? '1px solid var(--ft-hairline)' : 'none',
              }}
            >
              <span style={{ color: 'var(--ft-ink-3)', fontWeight: 600, flex: 'none' }}>{i + 1}</span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      </ShellRail>

      {/* Header */}
      <span className="ft-k brand">Trade Package</span>
      <h1 className="ft-hero" style={{ fontSize: 30 }}>
        {isCreator ? `To ${recipientName}.` : `${safeCreatorName} built this for you.`}
      </h1>
      <div className="ft-pillrow" style={{ marginTop: 14 }}>
        {leagueName ? (
          <span className="ft-chip">
            <SleeperAvatar url={tradePackage.league?.avatarUrl} name={leagueName} size={16} />
            <span>{leagueName}</span>
          </span>
        ) : null}
        <span className="ft-chip outline">{trades.length} option{trades.length === 1 ? '' : 's'}</span>
        {tradePackage.createdAt ? (
          <span className="ft-chip outline">Sent {fmtDate(tradePackage.createdAt)}</span>
        ) : null}
        {statusChip}
      </div>

      {/* Note from the creator */}
      {tradePackage.senderMessage ? (
        <div className="ft-card white" style={{ marginTop: 24, maxWidth: 560 }}>
          <span className="ft-k">Note from {isCreator ? 'you' : safeCreatorName}</span>
          <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {tradePackage.senderMessage}
          </p>
        </div>
      ) : null}

      {/* Creator share row */}
      {shouldShowShareLink && (
        <div style={{ marginTop: 28 }}>
          <span className="ft-k">Share it with {recipientName}</span>
          <div
            className="ftpk-share"
            style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, maxWidth: 620 }}
          >
            <span
              style={{
                flex: '1 1 auto', minWidth: 0, background: 'var(--ft-surface)', borderRadius: 'var(--ft-r-pill)',
                padding: '10px 16px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12.5, color: 'var(--ft-ink-2)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {window.location.href}
            </span>
            <button className="ft-btn sm" style={{ flex: 'none' }} onClick={handleCopyLink}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--ft-ink-3)' }}>
            They grade each option 1 to 10 — no account needed.
          </p>
        </div>
      )}

      {/* Recipient grade CTA */}
      {!isCreator && shouldShowRating && (
        <div style={{ marginTop: 28 }}>
          <button className="ft-btn ftpk-cta" onClick={handleStartRating}>Grade the options</button>
          <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--ft-ink-3)' }}>
            One at a time. About a minute.
          </p>
        </div>
      )}

      {/* Creator: counter-proposal needs action */}
      {showCreatorStatus && status === 'counter-proposed' && (
        <div className="ft-card white" style={{ marginTop: 28, borderLeft: '3px solid var(--ft-brand)' }}>
          <span className="ft-k brand">Action required</span>
          <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.5 }}>
            {recipientName} graded your options and sent a counter. Their feedback is below — grade the counter to keep it moving.
          </p>
          <button className="ft-btn sm ftpk-cta" style={{ marginTop: 14 }} onClick={handleStartRating}>
            Rate the counter-proposal
          </button>
        </div>
      )}

      {/* Creator: round 1 history (their grades on the original offer) */}
      {showCreatorStatus && hasHistory && (
        <div className="ft-section">
          <span className="ft-k">Round 1 — your original offer</span>
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--ft-ink-2)' }}>
            How {recipientName} graded what you sent.
          </p>
          <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {tradePackage.originalGrades.map((gradeData, index) => {
              const trade = gradeData.trade || {};
              const verdict = getVerdict(gradeData.gradeValue);
              return (
                <div className="ft-card white" key={index} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <span className="ft-k">Option {index + 1}</span>
                    <span className="ft-chip dark">{gradeData.gradeValue}/10</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <span className="ft-k" style={{ fontSize: 10 }}>You offered</span>
                      {(trade.creatorItems || []).map((item, idx) => (
                        <div key={idx} style={{ fontSize: 13.5, fontWeight: 500, marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {itemLabel(item, playerData)}
                        </div>
                      ))}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <span className="ft-k" style={{ fontSize: 10 }}>For</span>
                      {(trade.recipientItems || []).map((item, idx) => (
                        <div key={idx} style={{ fontSize: 13.5, fontWeight: 500, marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {itemLabel(item, playerData)}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid var(--ft-hairline)', paddingTop: 10,
                      ...(verdict.tone === 'hate' ? { borderLeft: '3px solid var(--ft-red)', paddingLeft: 10 } : {}),
                      ...(verdict.tone === 'love' ? { borderLeft: '3px solid var(--ft-green)', paddingLeft: 10 } : {}),
                    }}
                  >
                    <span style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em' }}>{verdict.word}</span>
                    {gradeData.notes ? (
                      <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--ft-ink-2)', fontStyle: 'italic', lineHeight: 1.45 }}>
                        “{gradeData.notes}”
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trade option cards */}
      <div className="ft-section">
        <span className="ft-k">{optionsHeading}</span>
        {status === 'graded' && !hasHistory && isCreator ? (
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--ft-ink-2)' }}>
            {recipientName} graded every option. The grades and notes are on each card.
          </p>
        ) : null}
        <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
          {trades.map((trade, index) => (
            <TradeThumbnailCard
              key={trade.id}
              trade={trade}
              playerData={playerData}
              league={tradePackage.league}
              index={index}
              viewerRole={viewerRole}
            />
          ))}
        </div>
      </div>

      {status === 'graded' && (
        <div className="ft-section" style={{ maxWidth: 720 }}>
          <UpsellCard
            wordmark="Analyst Review"
            icon={ReviewIcon}
            title="Want a professional's take?"
            body="A real dynasty analyst breaks down this trade for both sides — value, timeline fit, and what it means long-term."
            cta="Get it reviewed"
            to="/analyst/fantasynerdboi"
          />
        </div>
      )}
    </div>
  );
};

export default PackageView;
